import React, {
  useEffect, useRef, useState, useCallback,
} from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { updateDisplayStyle, getStaticStateClasses } from './utils/actions';
import { ANIMATION_STATE_CLASSES, CSS_PROPERTIES } from './constants';
import useHeightTransitions from './hoc/use-height-transitions';
import './animate-height.scss';

/* eslint-disable react/jsx-props-no-spreading */
const AnimateHeight = React.forwardRef(({
  applyInlineTransitions, children, className, contentClassName, delay, contentRef, duration,
  easing, height, onHeightAnimationEnd, onHeightAnimationStart, style,
}, ref) => {
  const contentElement = useRef(null);
  const animationClassesTimeoutID = useRef(null);
  const timeoutID = useRef(null);
  const stateClasses = useRef(ANIMATION_STATE_CLASSES);
  const [currentHeight, setCurrentHeight] = useState(height);
  const [overflow, setOverflow] = useState(CSS_PROPERTIES.HIDDEN);
  const [useTransitions, setUseTransitions] = useState(false);
  const [animationStateClassNames, setAnimationStateClassNames] = useState(
    getStaticStateClasses(stateClasses.current, height),
  );
  const contentRefCallback = useCallback((element) => {
    contentElement.current = element;

    if (contentRef) {
      const reference = contentRef;
      reference.current = element;
    }
  }, []);

  useEffect(() => {
    updateDisplayStyle(contentElement.current, currentHeight, CSS_PROPERTIES.NONE);
  }, []);

  useEffect(() => {
    if (contentElement.current) {
      // set total animation time
      const totalDuration = duration + delay;
      const newHeight = height < 0 ? 0 : height;
      const timeoutHeight = newHeight;

      // Animation classes
      const newAnimationStateClassNames = [
        stateClasses.current.animating,
        stateClasses.current.animatingToHeightSpecific,
      ].filter((v) => v).join(' ');

      // Animation classes to be put after animation is complete
      const timeoutAnimationStateClasses = getStaticStateClasses(
        stateClasses.current,
        timeoutHeight,
      );

      // Set starting height and animating classes
      // When animating from 'auto' we first need to set fixed height
      // that change should be animated
      setCurrentHeight(newHeight);
      setOverflow(CSS_PROPERTIES.HIDDEN);
      setUseTransitions(true);
      setAnimationStateClassNames(newAnimationStateClassNames);

      // Clear timeouts
      clearTimeout(timeoutID.current);
      clearTimeout(animationClassesTimeoutID.current);

      // ANIMATION STARTS, run a callback if it exists
      onHeightAnimationStart?.(newHeight);

      // Set end height, classes and remove transitions when animation is complete
      timeoutID.current = setTimeout(() => {
        setCurrentHeight(timeoutHeight);
        setUseTransitions(false);
        setAnimationStateClassNames(timeoutAnimationStateClasses);

        // Run a callback if it exists
        onHeightAnimationEnd?.(newHeight);
      }, totalDuration);
    }

    return () => {
      clearTimeout(timeoutID.current);
      clearTimeout(animationClassesTimeoutID.current);
    };
  }, [height]);

  const componentStyle = {
    ...style,
    height: currentHeight,
    overflow: overflow || style?.overflow,
  };

  if (useTransitions && applyInlineTransitions) {
    componentStyle.transition = `height ${duration}ms ${easing} ${delay}ms`;

    // Include transition passed through styles
    if (style?.transition) {
      componentStyle.transition = `${style.transition}, ${componentStyle.transition}`;
    }

    // Add webkit vendor prefix still used by opera, blackberry...
    componentStyle.WebkitTransition = componentStyle.transition;
  }

  return (
    <div
      ref={ref}
      style={componentStyle}
      aria-hidden={height === 0}
      className={clsx('animate-height-cont', animationStateClassNames, className)}
    >
      <div ref={contentRefCallback} className={contentClassName}>
        {children}
      </div>
    </div>
  );
});

AnimateHeight.defaultProps = {
  delay: 0,
  style: null,
  className: '',
  duration: 500,
  children: null,
  easing: 'ease',
  height: 'auto',
  contentRef: null,
  contentClassName: '',
  animationStateClasses: {},
  applyInlineTransitions: true,
  onHeightAnimationEnd: () => {},
  onHeightAnimationStart: () => {},
};

AnimateHeight.propTypes = {
  children: PropTypes.node,
  delay: PropTypes.number,
  easing: PropTypes.string,
  style: PropTypes.shape({
    overflow: PropTypes.string,
    transition: PropTypes.string,
  }),
  duration: PropTypes.number,
  className: PropTypes.string,
  contentRef: PropTypes.shape({}),
  contentClassName: PropTypes.string,
  onHeightAnimationEnd: PropTypes.func,
  applyInlineTransitions: PropTypes.bool,
  onHeightAnimationStart: PropTypes.func,
  animationStateClasses: PropTypes.shape({}),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default useHeightTransitions(AnimateHeight);
